import {
  parseMediaFilters,
  setMediaFilter,
  type MediaLibraryFilters,
} from "@/hooks/useMediaLibrary";

/**
 * Model okna výběru fotek (vize podnety-tymu-2026-10, F7.9–F7.11; řez 05, Kontrakt bod 3):
 * stav dlaždice, výběr, výchozí a zúžené filtry okna, Esc. Čisté funkce bez Reactu; volá je
 * `MediaPicker` (K2) a mřížka okna (`PickerGrid`, K1).
 *
 * Signatury a typy klade Kontrakt řezu 05. Tentýž stav dlaždice (`pickerTileState`) čte
 * mřížka i prohlížeč v okně (`pick.stateOf`), takže dlaždice a „Vybrat“ se nerozejdou.
 */

/** Režim okna; režim `gallery` pole formuláře mapuje `MediaPicker` (K2) na `"multiple"`. */
export type PickerMode = "single" | "multiple";

/**
 * Stav dlaždice: `selectable` | `selected` (vybraná v okně) | `in-gallery` (fotka už je
 * v hodnotě vícenásobného pole, vybrat nejde) | `current` (aktuální hodnota jednoduchého
 * výběru, štítek „Vybraná“).
 */
export type PickerTileState = "selectable" | "selected" | "in-gallery" | "current";

export interface PickerContext {
  mode: PickerMode;
  /** Hodnota pole formuláře (jednoduchý výběr: nejvýš jedno ID). */
  value: readonly string[];
  /** Vybrané v okně, zatím nepřidané. */
  picked: readonly string[];
}

/**
 * Jednoduchý výběr: `current` jen u aktuální hodnoty. Vícenásobný: fotka v hodnotě pole je
 * `in-gallery` s předností před výběrem okna (vybrat nejde ani klikem, ani v prohlížeči).
 */
export function pickerTileState(id: string, ctx: PickerContext): PickerTileState {
  if (ctx.mode === "single") return ctx.value[0] === id ? "current" : "selectable";
  if (ctx.value.includes(id)) return "in-gallery";
  return ctx.picked.includes(id) ? "selected" : "selectable";
}

/**
 * Klik na dlaždici ve vícenásobném výběru: vybraná se odebere, vybratelná se připojí na
 * konec (pořadí výběru = pořadí přidání do galerie). Jednoduchý výběr a fotka v galerii
 * vrátí tutéž referenci `ctx.picked`, ať volající nic nepřekreslí.
 */
export function togglePicked(ctx: PickerContext, id: string): string[] {
  const picked = ctx.picked as string[];
  if (ctx.mode === "single") return picked;
  const state = pickerTileState(id, ctx);
  if (state === "selected") return picked.filter((p) => p !== id);
  if (state === "selectable") return [...picked, id];
  return picked;
}

/**
 * Jediné místo pravidla „připoj jen to, co ještě nikde není“: z `extra` v jeho pořadí každé
 * ID, které není v `seen` ani dřív v `extra`. Nese AK7 (nic se neodebere ani nepřeřadí)
 * i vyřazení „V galerii“ po dávce.
 */
function unseenIds(extra: readonly string[], seen: Iterable<string>): string[] {
  const known = new Set(seen);
  const added: string[] = [];
  for (const id of extra) {
    if (known.has(id)) continue;
    known.add(id);
    added.push(id);
  }
  return added;
}

/**
 * Fotky nahrané dávkou v okně do výběru: v pořadí dávky každé ID, které není v hodnotě,
 * ve výběru ani dřív v dávce. Bez přírůstku (i v jednoduchém výběru) tatáž reference.
 */
export function addPicked(ctx: PickerContext, ids: readonly string[]): string[] {
  const picked = ctx.picked as string[];
  if (ctx.mode === "single") return picked;
  const added = unseenIds(ids, [...ctx.value, ...ctx.picked]);
  return added.length === 0 ? picked : [...picked, ...added];
}

/** Galerie po „Přidat vybrané“: dosavadní beze změny a v pořadí, za ní nové v pořadí výběru. */
export function galleryAfterAdd(value: readonly string[], picked: readonly string[]): string[] {
  return [...value, ...unseenIds(picked, value)];
}

/**
 * Počáteční parametry okna: jen země editoru. Jediné místo, kde se mění, co okno při otevření
 * předvyplní. Druh (`folder` editoru) se NEpředvyplňuje — redakce s ním fotky z jiných
 * editorů neviděla, filtr zapomínala vypnout a nahrávala je znovu (2026-10-30); druh editoru
 * dál určuje jen složku nově nahraných fotek.
 */
export function pickerInitialParams(countryId: string | null): URLSearchParams {
  const params = new URLSearchParams();
  if (countryId) params.set("country", countryId);
  return params;
}

/**
 * touched true → vrátí params beze změny; jinak kopii params s country = countryId (při null
 * klíč country smazaný), ostatní klíče a jejich pořadí beze změny; idempotentní:
 * f(f(p, c, false), c, false).toString() === f(p, c, false).toString(). K2 porovnává jen
 * get("country").
 */
export function pickerParamsForScope(
  params: URLSearchParams,
  countryId: string | null,
  touched: boolean,
): URLSearchParams {
  if (touched) return params;
  return setMediaFilter(params, "country", countryId);
}

/**
 * Filtry okna z jeho místních parametrů: vždy `sort: "starred"`, nikdy koš. `trash=1` by
 * `parseMediaFilters` přečetl (stránka Média ho tak nese) a smazaná fotka by se dostala do
 * galerie, proto ho okno zahodí bez ohledu na to, odkud se do parametrů dostal.
 */
export function pickerFilters(params: URLSearchParams): MediaLibraryFilters {
  const { trash: _trash, ...rest } = parseMediaFilters(params);
  return { ...rest, sort: "starred" };
}

/** Esc zavře okno, jen když ho nezpracoval někdo jiný, zavírání neběží a prohlížeč není otevřený. */
export function shouldCloseOnEscape(
  e: { key: string; defaultPrevented: boolean },
  s: { closePending: boolean; viewerOpen: boolean },
): boolean {
  return e.key === "Escape" && !e.defaultPrevented && !s.closePending && !s.viewerOpen;
}
